import { useTranslation } from '@silospot/i18n/react';
import type { CloudflareCapabilities } from '@silospot/shared';
import { toast } from '@silospot/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Loader2, X } from 'lucide-react';
import { type ReactElement, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CfBulkImport } from '../../components/cld/cf-bulk-import';
import { CfFlow } from '../../components/cld/cf-flow';
import { CfRulesView } from '../../components/cld/cf-rules-view';
import { Badge, Btn, Card, CardHead, PageHeader } from '../../components/cld/kit';
import { api, serverError } from '../../lib/api';
import { parseUtc } from '../../lib/datetime';

const PLAN_WAF_MAX: Record<string, number> = {
  free: 5,
  pro: 20,
  business: 100,
  enterprise: 1000,
  unknown: 5,
};

export function CfConnectPage(): ReactElement {
  const { t, i18n } = useTranslation('pages');
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const [redo, setRedo] = useState(false);
  const [discAsk, setDiscAsk] = useState(false);
  const [checklist, setChecklist] = useState<CloudflareCapabilities | null>(null);

  const statusQ = useQuery({
    queryKey: ['cf-status', id],
    queryFn: () => api.cfStatus(id),
    enabled: !!id,
  });
  const journalQ = useQuery({
    queryKey: ['cf-journal', id],
    queryFn: () => api.cfJournal(id),
    enabled: !!id && !!statusQ.data?.connected,
  });
  const s = statusQ.data;

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['cf-status', id] });
    void qc.invalidateQueries({ queryKey: ['cf-journal', id] });
  };

  const test = useMutation({
    mutationFn: () => api.cfProvision(id, false),
    onSuccess: (r) => {
      if (r.capabilities) setChecklist(r.capabilities);
      toast.success(t('cfp.testDone'));
      refresh();
    },
    onError: (e) => {
      // A dead/revoked token returns a clean 400 (code cf_token_invalid): tell the
      // user plainly and open the reconnect flow instead of a raw error.
      if ((e as { data?: { code?: string } } | null)?.data?.code === 'cf_token_invalid') {
        toast.error(t('cfp.tokenExpired'));
        setRedo(true);
        return;
      }
      toast.error(serverError(e) ?? t('cf.errorGeneric'));
    },
  });
  const unblock = useMutation({
    mutationFn: (ip: string) => api.cfUnblock(id, ip),
    onSuccess: () => {
      toast.success(t('cfp.unblocked'));
      refresh();
    },
  });

  // Multi-select bulk unblock over the journal's active entries. Chunks of 50,
  // resume on error, single toast with the tally.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkUnblocking, setBulkUnblocking] = useState(false);
  const toggleSel = (ip: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(ip) ? next.delete(ip) : next.add(ip);
      return next;
    });
  const bulkUnblock = async () => {
    const targets = [...selected];
    if (targets.length === 0) return;
    setBulkUnblocking(true);
    let ok = 0;
    let fail = 0;
    for (let i = 0; i < targets.length; i += 50) {
      const chunk = targets.slice(i, i + 50);
      try {
        const r = await api.cfBulkUnblock(id, chunk);
        for (const res of r.results) {
          if (res.ok) ok += 1;
          else fail += 1;
        }
      } catch {
        fail += chunk.length;
      }
    }
    setBulkUnblocking(false);
    setSelected(new Set());
    toast.success(t('cfp.bulkUnblockToast', { ok, fail }));
    refresh();
  };
  const disconnect = useMutation({
    mutationFn: (mode: 'token-only' | 'with-resources') => api.cfDisconnect(id, mode),
    onSuccess: () => {
      setDiscAsk(false);
      toast.info(t('cfp.disconnected'));
      refresh();
    },
  });

  // Auto-block scheduler: enable (dry-run first) / promote to live / disable. The
  // preview shows exactly what would be pushed + the account-wide fill vs the cap.
  const setAuto = useMutation({
    mutationFn: (body: { enabled: boolean; dryRun?: boolean }) => api.cfSetAuto(id, body),
    onSuccess: () => {
      toast.success(t('cfp.autoSaved'));
      void qc.invalidateQueries({ queryKey: ['cf-auto-preview', id] });
      refresh();
    },
    onError: (e) => toast.error(serverError(e) ?? t('cf.errorGeneric')),
  });
  const autoPreviewQ = useQuery({
    queryKey: ['cf-auto-preview', id],
    queryFn: () => api.cfAutoPreview(id),
    enabled: !!id && !!statusQ.data?.connected,
  });

  // Cloudflare V3 — tracker injection via Zaraz (Palier E, method B), managed
  // right here on the connected page. The toggle is gated by the token carrying
  // Zaraz:Edit; without it we explain how to widen the token rather than fail.
  const zarazQ = useQuery({
    queryKey: ['cf-zaraz', id],
    queryFn: () => api.zarazStatus(id),
    enabled: !!id && !!statusQ.data?.connected,
    retry: false,
  });
  // Never mute an injection error: a known precondition code → its localized
  // reason; otherwise the server's real message (serverError), then a generic.
  const zarazErr = (e: unknown): string => {
    const m = (e as { message?: string } | null)?.message;
    if (m === 'noproxy' || m === 'noperm' || m === 'quota' || m === 'disabled')
      return t(`ti.zErr_${m}_b`, { site: s?.zoneName ?? '' });
    return serverError(e) ?? t('cf.errorGeneric');
  };
  const zarazOn = useMutation({
    mutationFn: () => api.zarazActivate(id),
    onSuccess: () => {
      toast.success(t('cfp.zarazActivated'));
      void qc.invalidateQueries({ queryKey: ['cf-zaraz', id] });
    },
    onError: (e) => toast.error(zarazErr(e)),
  });
  const zarazOff = useMutation({
    mutationFn: () => api.zarazDeactivate(id),
    onSuccess: () => {
      toast.info(t('cfp.zarazDeactivated'));
      void qc.invalidateQueries({ queryKey: ['cf-zaraz', id] });
    },
    onError: (e) => toast.error(zarazErr(e)),
  });

  const crumb = (
    <div className="mb-3 flex items-center gap-1.5 text-[12.5px] text-text-3">
      <span>{t('cfp.settings')}</span>
      <span>/</span>
      <span className="text-text-2">Cloudflare</span>
    </div>
  );

  if (statusQ.isLoading) {
    return <div className="h-40 animate-pulse rounded-2xl bg-neutral-bg" />;
  }

  if (!s?.connected || redo) {
    return (
      <div>
        {crumb}
        <PageHeader title={t('cfp.connectTitle')} sub={t('cfp.connectSub')} />
        <Card>
          <CfFlow
            projectId={id}
            projectDomain={s?.zoneName || ''}
            onDone={() => {
              setRedo(false);
              refresh();
            }}
          />
        </Card>
      </div>
    );
  }

  const wafMax = PLAN_WAF_MAX[s.plan] ?? 5;
  const ruleKpi = s.ruleId
    ? `1 / ${wafMax}`
    : s.fallbackMode === 'ip_access_rules'
      ? t('cfp.fallbackShort')
      : '—';
  const listKpi =
    s.fallbackMode === 'ip_access_rules' ? t('cfp.fallbackShort') : `${s.listCount} / 10 000`;
  const modeKpi =
    s.mode === 'auto'
      ? t('cf.modeAutoShort')
      : s.mode === 'challenge'
        ? t('cf.modeChallengeShort')
        : t('cf.modeManualShort');
  const df = new Intl.DateTimeFormat(i18n.language || 'fr', {
    dateStyle: 'short',
    timeStyle: 'short',
    timeZone: 'Europe/Brussels',
  });
  const at = (v: string): Date => parseUtc(v);

  const KPIS: Array<[string, string, string]> = [
    [t('cfp.kpiZone'), s.zoneName, s.proxied ? t('cfp.proxyOn') : t('cfp.proxyOff')],
    [t('cfp.kpiToken'), t('cfp.tokenValid'), s.lastCheckAt ? df.format(at(s.lastCheckAt)) : '—'],
    [t('cfp.kpiRule'), ruleKpi, t('cfp.kpiRuleSub', { plan: s.plan })],
    [t('cfp.kpiList'), listKpi, t('cfp.kpiListSub')],
    [t('cfp.kpiMode'), modeKpi, s.manualAllowed ? t('cfp.manualOn') : t('cfp.manualOff')],
  ];

  const CHECK_ITEMS = checklist
    ? [
        [t('cfp.ckToken'), checklist.tokenValid],
        [t('cfp.ckZone'), !!checklist.matchedZone?.active],
        [t('cfp.ckWaf'), checklist.waf.readable],
        [t('cfp.ckList'), checklist.list.readable],
        [
          t('cfp.ckQuota'),
          !checklist.waf.atCapacity || checklist.fallbackMode === 'ip_access_rules',
        ],
        [t('cfp.ckProxy'), checklist.proxied],
      ]
    : [];

  return (
    <div>
      {crumb}
      <PageHeader
        title={t('cfp.title')}
        sub={
          s.activeIps + s.activeRanges > 0
            ? t('cfp.subActive', {
                ips: t('units.ip', { count: s.activeIps }),
                ranges: t('units.range', { count: s.activeRanges }),
              })
            : t('cfp.subNone')
        }
        actions={
          <>
            <Btn disabled={test.isPending} onClick={() => test.mutate()}>
              {test.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {t('cfp.test')}
            </Btn>
            <Btn onClick={() => setRedo(true)}>{t('cfp.renew')}</Btn>
            <Btn danger onClick={() => setDiscAsk(true)}>
              {t('cfp.disconnect')}
            </Btn>
          </>
        }
      />

      <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-5">
        {KPIS.map((c, i) => (
          <Card key={c[0]} className="p-4">
            <div className="text-[12px] text-text-2">{c[0]}</div>
            <div
              className={`mt-1.5 text-[15px] font-bold ${i === 1 ? 'text-clean-fg' : 'text-text'}`}
            >
              {c[1]}
            </div>
            <div className="mt-0.5 text-[11.5px] text-text-3">{c[2]}</div>
          </Card>
        ))}
      </div>

      {checklist ? (
        <Card className="mb-4">
          <CardHead title={t('cfp.checklistTitle')} sub={t('cfp.checklistSub')} />
          <div className="flex flex-wrap gap-2">
            {CHECK_ITEMS.map(([label, ok]) => (
              <span
                key={String(label)}
                className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-[12.5px] ${ok ? 'border-clean-border bg-clean-bg text-clean-fg' : 'border-[#f3c9c9] bg-fraud-bg text-fraud-fg'}`}
              >
                {ok ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}
                {label}
              </span>
            ))}
          </div>
        </Card>
      ) : null}

      <Card className="mb-4">
        <CardHead title={t('cfp.autoTitle')} sub={t('cfp.autoSub')} />
        {s.mode !== 'auto' ? (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="max-w-xl text-[12.5px] leading-relaxed text-text-2">
              {t('cfp.autoOffBody')}
            </p>
            <Btn
              primary
              disabled={setAuto.isPending}
              onClick={() => setAuto.mutate({ enabled: true, dryRun: true })}
            >
              {setAuto.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {t('cfp.autoEnable')}
            </Btn>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2.5">
              <span
                className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-[12.5px] ${s.autoDryRun ? 'border-[#f3dca6] bg-[#fdf6e8] text-[#7c5212]' : 'border-clean-border bg-clean-bg text-clean-fg'}`}
              >
                {s.autoDryRun ? t('cfp.autoDryBadge') : t('cfp.autoLiveBadge')}
              </span>
              <span className="text-[12px] text-text-3">
                {t('cfp.autoThreshold', { n: autoPreviewQ.data?.threshold ?? s.threshold })}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              <div className="rounded-lg border border-border bg-bg/40 p-3">
                <div className="text-[11.5px] text-text-3">{t('cfp.autoKpiCandidates')}</div>
                <div className="mt-0.5 text-[15px] font-bold text-text">
                  {autoPreviewQ.data?.candidates ?? '—'}
                </div>
              </div>
              <div className="rounded-lg border border-border bg-bg/40 p-3">
                <div className="text-[11.5px] text-text-3">{t('cfp.autoKpiBlocked')}</div>
                <div className="mt-0.5 text-[15px] font-bold text-text">
                  {`${t('units.ip', { count: s.activeIps })} · ${t('units.range', { count: s.activeRanges })}`}
                </div>
              </div>
              <div className="rounded-lg border border-border bg-bg/40 p-3">
                <div className="text-[11.5px] text-text-3">{t('cfp.autoKpiFill')}</div>
                <div className="mt-0.5 text-[15px] font-bold text-text">
                  {autoPreviewQ.data
                    ? `${autoPreviewQ.data.fill.count} / ${autoPreviewQ.data.fill.cap.toLocaleString(i18n.language || 'fr')}`
                    : '—'}
                </div>
              </div>
            </div>
            {autoPreviewQ.data && autoPreviewQ.data.sample.length > 0 ? (
              <div>
                <div className="mb-1 text-[11.5px] text-text-3">{t('cfp.autoSample')}</div>
                <div className="flex flex-wrap gap-1.5">
                  {autoPreviewQ.data.sample.slice(0, 12).map((c) => (
                    <span
                      key={c.target}
                      className="rounded-md bg-neutral-bg px-2 py-0.5 font-mono text-[11.5px] text-text-2"
                    >
                      {c.target}
                    </span>
                  ))}
                </div>
              </div>
            ) : null}
            {autoPreviewQ.data &&
            autoPreviewQ.data.filtered.infra +
              autoPreviewQ.data.filtered.residential +
              autoPreviewQ.data.filtered.goodBot >
              0 ? (
              <p className="text-[11.5px] leading-relaxed text-clean-fg">
                {t('cfp.autoFiltered', {
                  n:
                    autoPreviewQ.data.filtered.infra +
                    autoPreviewQ.data.filtered.residential +
                    autoPreviewQ.data.filtered.goodBot,
                })}
              </p>
            ) : null}
            <p className="text-[11.5px] leading-relaxed text-text-3">{t('cfp.autoDoctrine')}</p>
            <div className="flex flex-wrap gap-2">
              {s.autoDryRun ? (
                <Btn
                  primary
                  disabled={setAuto.isPending}
                  onClick={() => setAuto.mutate({ enabled: true, dryRun: false })}
                >
                  {t('cfp.autoPromote')}
                </Btn>
              ) : (
                <Btn
                  disabled={setAuto.isPending}
                  onClick={() => setAuto.mutate({ enabled: true, dryRun: true })}
                >
                  {t('cfp.autoBackToDry')}
                </Btn>
              )}
              <Btn
                danger
                disabled={setAuto.isPending}
                onClick={() => setAuto.mutate({ enabled: false })}
              >
                {t('cfp.autoDisable')}
              </Btn>
            </div>
          </div>
        )}
      </Card>

      <Card className="mb-4">
        <CardHead title={t('cfp.tokenCardTitle')} sub={t('cfp.tokenCardSub')} />
        <div className="flex flex-wrap items-center gap-2.5">
          <span className="rounded-lg bg-neutral-bg px-3 py-1.5 font-mono text-[13px]">
            ••••{s.tokenLast4 || '····'}
          </span>
          <Badge tone="info">{t('cf.permZone')}</Badge>
          <Badge tone="info">{t('cf.permWafEdit')}</Badge>
          <Badge tone="info">{t('cf.permFw')}</Badge>
          <Badge tone="info">{t('cf.permList')}</Badge>
          <Badge tone="neutral">{t('cfp.zoneScoped')}</Badge>
          <span className="text-[12px] text-text-3">{t('cfp.revocable')}</span>
        </div>
      </Card>

      <Card className="mb-4">
        <CardHead title={t('cfp.zarazTitle')} sub={t('cfp.zarazSub')} />
        {zarazQ.isLoading ? (
          <div className="h-12 animate-pulse rounded bg-neutral-bg" />
        ) : !zarazQ.data?.hasPerm ? (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[#f3dca6] bg-[#fdf6e8] px-3.5 py-3 text-[12.5px] leading-relaxed text-[#7c5212]">
            <span className="flex-1">{t('cfp.zarazNoPerm')}</span>
            <Btn onClick={() => setRedo(true)}>{t('cfp.renew')}</Btn>
          </div>
        ) : (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2.5">
              <span
                className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-[12.5px] ${zarazQ.data?.injected ? 'border-clean-border bg-clean-bg text-clean-fg' : 'border-border bg-neutral-bg text-text-2'}`}
              >
                {zarazQ.data?.injected ? <Check className="h-3.5 w-3.5" /> : null}
                {zarazQ.data?.injected ? t('cfp.zarazInjected') : t('cfp.zarazNotInjected')}
              </span>
              <span className="text-[12px] text-text-3">{t('cfp.zarazHint')}</span>
            </div>
            {zarazQ.data?.injected ? (
              <Btn danger disabled={zarazOff.isPending} onClick={() => zarazOff.mutate()}>
                {zarazOff.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {t('cfp.zarazDeactivate')}
              </Btn>
            ) : (
              <Btn disabled={zarazOn.isPending} onClick={() => zarazOn.mutate()}>
                {zarazOn.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {t('cfp.zarazActivate')}
              </Btn>
            )}
          </div>
        )}
      </Card>

      <CfRulesView projectId={id} />

      <CfBulkImport projectId={id} listCount={s.listCount} onDone={refresh} />

      <Card className="p-0">
        <div className="flex flex-wrap items-center justify-between gap-2 px-[18px] pb-1 pt-4">
          <CardHead title={t('cfp.journalTitle')} sub={t('cfp.journalSub')} />
          {selected.size > 0 ? (
            <Btn danger disabled={bulkUnblocking} onClick={bulkUnblock}>
              {bulkUnblocking ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {t('cfp.bulkUnblockSel', { n: selected.size })}
            </Btn>
          ) : null}
        </div>
        <div className="px-[18px] pb-3">
          {journalQ.isLoading ? (
            <div className="h-20 animate-pulse rounded bg-neutral-bg" />
          ) : (journalQ.data?.actions.length ?? 0) === 0 ? (
            <p className="py-8 text-center text-[13px] text-text-3">{t('cfp.journalEmpty')}</p>
          ) : (
            journalQ.data?.actions.map((a, i) => (
              <div
                key={`${a.ip}-${a.at}-${i}`}
                className="flex flex-wrap items-center gap-2.5 border-b border-[#f1f3f6] py-2.5 last:border-0"
              >
                {a.status === 'active' && a.action !== 'unblock' ? (
                  <input
                    type="checkbox"
                    aria-label={a.ip}
                    checked={selected.has(a.ip)}
                    onChange={() => toggleSel(a.ip)}
                    className="h-3.5 w-3.5 accent-accent-strong"
                  />
                ) : (
                  <span className="w-3.5" />
                )}
                <span className="min-w-[128px] font-mono text-[12.5px] text-text">{a.ip}</span>
                <Badge
                  tone={
                    a.action === 'block' ? 'fraud' : a.action === 'challenge' ? 'suspect' : 'clean'
                  }
                >
                  {t(`cfp.act_${a.action}`)}
                </Badge>
                <span className="min-w-[160px] flex-1 text-[12px] text-text-3">
                  {t(`cfp.origin_${a.origin}`)}
                  {a.rule ? ` · ${a.rule}` : ''}
                  {a.detail ? ` · ${a.detail}` : ''}
                </span>
                <span className="text-[11.5px] text-text-3">{df.format(at(a.at))}</span>
                {a.status === 'active' && a.action !== 'unblock' ? (
                  <button
                    type="button"
                    disabled={unblock.isPending}
                    onClick={() => unblock.mutate(a.ip)}
                    className="rounded-md border border-border px-2.5 py-1 text-[12px] font-semibold text-accent-strong hover:bg-bg disabled:opacity-50"
                  >
                    {t('cfp.unblock')}
                  </button>
                ) : (
                  <span className="min-w-[52px] text-right text-[11.5px] text-text-3">
                    {a.status === 'removed' ? t('cfp.removed') : ''}
                  </span>
                )}
              </div>
            ))
          )}
        </div>
      </Card>

      {discAsk ? (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-[rgba(15,21,32,0.55)] p-4">
          <div className="w-full max-w-md rounded-2xl bg-surface p-6 shadow-xl">
            <h2 className="text-[18px] font-bold text-text">{t('cfp.discTitle')}</h2>
            <p className="mt-2 text-[13.5px] leading-relaxed text-text-2">{t('cfp.discBody')}</p>
            <div className="mt-5 flex flex-wrap justify-end gap-2">
              <Btn onClick={() => setDiscAsk(false)}>{t('cfp.cancel')}</Btn>
              <Btn
                disabled={disconnect.isPending}
                onClick={() => disconnect.mutate('with-resources')}
              >
                {t('cfp.discWithResources')}
              </Btn>
              <Btn
                danger
                disabled={disconnect.isPending}
                onClick={() => disconnect.mutate('token-only')}
              >
                {t('cfp.discTokenOnly')}
              </Btn>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
